/**
 * Why a dialled call didn't go out, and what the dialer offers next. The codes
 * come from server/lib/dialBlock; keep this in step with limecall-dashboard
 * src/lib/dialBlock.
 *
 * Most refusals of a softphone call are spoken in TwiML, and a carrier refusal
 * comes a second after the call "connected", so the browser hears none of them.
 * When the SDK reports a disconnect the voice provider asks
 * GET /api/voice/dial-outcome, and this turns the answer into a sentence and one
 * action.
 *
 * The message is the server's when it sent one, so copy can change without a
 * client deploy; the table here covers a block the dialer works out itself.
 * Never put a dialled number in a link: the ticket gets the country, its calling
 * code and the reason code, which is all support needs.
 */
import { getCountryCallingCode, type CountryCode } from 'libphonenumber-js';
import { kycCountryMessage } from './dialerCountryGate';

export interface DialBlock {
  code: string;
  action?: string;
  message?: string;
  /** ISO-2 country of the destination. */
  country?: string;
  countryName?: string;
  /** Calling code without the plus, e.g. "49". */
  dialCode?: string;
}

/** The two blocks that support lifts by enabling a country. */
export const COUNTRY_BLOCK_CODES = new Set(['carrier_country_disabled', 'country_not_enabled']);

/**
 * Does the dialer's own country gate already say what this refusal says? Only
 * then is the notice left out: a refusal for any other reason (credit, plan, a
 * paused account) has to show, with its own action, even while the gate blocks.
 */
export function gateRepeatsBlock(gateKind: string | null | undefined, code: string): boolean {
  if (gateKind === 'kyc' || gateKind === 'unavailable') return COUNTRY_BLOCK_CODES.has(code);
  return gateKind === 'trial' && code === 'trial_destination';
}

const FALLBACK_MESSAGES: Record<string, string> = {
  destination_restricted: "We can't place calls to this number.",
  destination_unverified: "We couldn't confirm this number can be called right now. Try again in a few minutes, or contact support if you need it.",
  destination_invalid: "That doesn't look like a valid phone number. Check it and try again.",
  destination_extra_digits: 'Remove any extension or pause digits from the number, then try again.',
  account_paused: 'Outbound calling is paused on this account. Contact support to get it turned back on.',
  call_failed: "That call didn't connect. Try again, or contact support if it keeps happening.",
};

/** A country name from its ISO code; no lookup table to fall out of date. */
export function countryNameOf(iso: string | null | undefined): string | undefined {
  if (!iso) return undefined;
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(iso) || iso;
  } catch {
    return iso;
  }
}

/** Calling code without the plus, or undefined for a code libphonenumber doesn't know. */
export function dialCodeOf(iso: string | null | undefined): string | undefined {
  if (!iso) return undefined;
  try {
    return String(getCountryCallingCode(iso as CountryCode));
  } catch {
    return undefined;
  }
}

export const countryBlockedMessage = (name?: string) => `Calls to ${name || 'that country'} aren't enabled on your account yet.`;
export const unblockLabel = (name?: string) => `Contact support to unblock ${name || 'this country'}`;

/** The ticket form, prefilled. Country, calling code and reason code only. */
export function supportHref(b: { code: string; country?: string | null; dialCode?: string | null }): string {
  const q = new URLSearchParams({ dial_block: b.code });
  if (b.country) q.set('country', b.country);
  const cc = b.dialCode || dialCodeOf(b.country);
  if (cc) q.set('cc', cc);
  return `/help?${q.toString()}`;
}

export type DialBlockCta =
  | { kind: 'link'; label: string; href: string }
  | { kind: 'verify'; label: string };

export interface DialBlockView {
  message: string;
  cta: DialBlockCta | null;
  /** A second way forward, shown as a plain link under the first. */
  alternative: { label: string; href: string } | null;
}

/**
 * What to show. `kycVerified` matters only for a country this account isn't
 * entitled to: an unverified account is asked to verify, then to ask support
 * (owner decision, see lib/dialerCountryGate). The message says both in order,
 * so the two controls are its two steps, and the link doesn't repeat it.
 */
export function dialBlockView(b: DialBlock, opts: { kycVerified?: boolean } = {}): DialBlockView {
  const name = b.countryName || countryNameOf(b.country);
  const isCountry = COUNTRY_BLOCK_CODES.has(b.code);
  const href = supportHref(b);
  const supportLabel = isCountry ? unblockLabel(name) : 'Contact support';

  if (b.code === 'country_not_enabled' && !opts.kycVerified) {
    return {
      message: kycCountryMessage(name),
      cta: { kind: 'verify', label: 'Verify ID' },
      alternative: { label: 'Contact support', href },
    };
  }

  const message = isCountry ? countryBlockedMessage(name)
    : b.message || FALLBACK_MESSAGES[b.code] || FALLBACK_MESSAGES.call_failed;

  // A block the dialer built itself has no action; the code says which it is.
  const action = b.action ?? (/^destination_(invalid|extra)/.test(b.code) ? 'fix_number' : 'contact_support');
  let cta: DialBlockCta | null = null;
  switch (action) {
    case 'contact_support': cta = { kind: 'link', label: supportLabel, href }; break;
    case 'verify_identity': cta = { kind: 'verify', label: 'Verify ID' }; break;
    case 'upgrade': cta = { kind: 'link', label: 'See plans', href: '/settings/billing' }; break;
    case 'add_credit': cta = { kind: 'link', label: 'Add credit', href: '/settings/billing' }; break;
    case 'update_payment': cta = { kind: 'link', label: 'Update payment method', href: '/settings/billing' }; break;
    default: cta = null;
  }
  return { message, cta, alternative: null };
}

const CODE_RE = /^[a-z][a-z0-9_]{0,40}$/;

/**
 * Was this call refused? Null for "no", and for anything unknown: an error here
 * must leave the dialer exactly as it was.
 */
export async function lookupDialOutcome(
  callSid: string | null | undefined,
  get: (url: string) => Promise<any>,
): Promise<DialBlock | null> {
  if (!callSid || !/^CA[0-9a-f]{32}$/i.test(callSid)) return null;
  try {
    const r = await get(`/api/voice/dial-outcome/${callSid}`);
    const b = r?.blocked === true ? r.block : null;
    return b && typeof b.code === 'string' && CODE_RE.test(b.code) ? b : null;
  } catch {
    return null;
  }
}

/**
 * Read a /help?dial_block=… link into a ticket. Each value must look like what
 * supportHref writes: a link is outside text, and it must not put arbitrary
 * words in a form the customer sends as their own.
 */
export function ticketFromDialBlockLink(search: string): { subject: string; description: string } | null {
  const q = new URLSearchParams(search);
  const code = q.get('dial_block');
  if (!code || !CODE_RE.test(code)) return null;
  const rawCountry = q.get('country') || '';
  const country = /^[A-Z]{2}$/.test(rawCountry) ? rawCountry : null;
  const rawCc = q.get('cc') || '';
  const cc = /^\d{1,4}$/.test(rawCc) ? rawCc : null;
  const name = countryNameOf(country);
  const where = name ? `${name}${cc ? ` (+${cc})` : ''}` : cc ? `+${cc}` : '';
  const details = [country && `Country: ${country}`, cc && `Calling code: +${cc}`, `Reason code: ${code}`].filter(Boolean).join('\n');

  if (COUNTRY_BLOCK_CODES.has(code)) {
    return {
      subject: `Unblock calls to ${name || 'another country'}`,
      description: `Please enable outbound calls to ${where || 'this country'} on my account.\n\n${details}`,
    };
  }
  return {
    subject: where ? `Help with a blocked call to ${name || where}` : 'Help with a blocked call',
    description: `A call I tried to place from the dialer didn't go through${where ? ` (${where})` : ''}.\n\n${details}`,
  };
}
